// frontend/src/components/dashboard/SiteWeatherSection.js
import React, { useMemo } from 'react';
import { Sun, CloudRain, Thermometer, Droplets, MapPin, AlertTriangle, Users } from 'lucide-react';
import useSiteWeather from '../../hooks/useSiteWeather';
import { StatusBadge } from '../UI/StatusBadge';
import { CountBadge } from '../UI/CountBadge';
import { formatRelativeTime } from '../../utils/dateUtils';

const HEAT_ORDER = { extreme: 3, high: 2, moderate: 1, low: 0, unknown: -1 };

const HEAT_STATUS = {
  extreme: 'heat_extreme',
  high: 'heat_high',
  moderate: 'heat_moderate',
  low: 'heat_low',
  unknown: 'heat_unknown',
};

const fmtTemp = (v) => (v === null || v === undefined ? '–' : `${Math.round(v)}°C`);

const fmtCount = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return '0';
  return Math.round(num).toLocaleString('en-SG');
};

const headerAdvisory = (summary, maxLevel) => {
  const exposed = Number(summary.workers_high_heat) || 0;
  if (exposed > 0) {
    return {
      status: maxLevel === 'extreme' ? 'heat_extreme' : 'heat_high',
      label: `${fmtCount(exposed)} worker${exposed === 1 ? '' : 's'} exposed`,
    };
  }
  if (maxLevel === 'moderate') {
    return { status: 'heat_moderate' };
  }
  return { status: 'heat_clear' };
};

/**
 * Shared `.metrics-card` primitive (same as Levy / Work Pass).
 * Always horizontal — these are short headcounts, not the currency
 * long-value exception that stacks the icon on 2-up mobile.
 */
const SummaryCard = ({
  icon: Icon,
  iconBg,
  iconFg,
  value,
  label,
  detail,
  title,
  valueClass = '',
}) => (
  <div className="metrics-card h-full" title={title || label}>
    <div className={`metrics-card-content${detail ? ' items-start' : ''}`}>
      <div className={`metrics-card-icon ${iconBg}`}>
        <Icon className={`w-4 h-4 sm:w-6 sm:h-6 ${iconFg}`} aria-hidden="true" />
      </div>
      <div className="metrics-card-text">
        <p className={`metrics-card-value truncate tabular-nums ${valueClass}`.trim()}>{value}</p>
        <p className="metrics-card-label">{label}</p>
        {detail ? (
          <p className="text-[11px] sm:text-xs text-gray-500 mt-1 truncate">{detail}</p>
        ) : null}
      </div>
    </div>
  </div>
);

/**
 * Site Weather & Heat Advisory — live NEA readings mapped onto the sites where
 * workers are CURRENTLY clocked in (with live headcount), sorted worst-heat
 * first. Answers "are my people exposed right now?" — outdoor-crew safety for
 * HR/Ops. Degrades gracefully when NEA is unreachable or nobody is on site.
 */
const SiteWeatherSection = () => {
  const { data, isLoading } = useSiteWeather();

  const sites = useMemo(() => {
    const list = data?.sites || [];
    return [...list].sort(
      (a, b) =>
        (HEAT_ORDER[b?.heat_risk?.level] ?? -1) - (HEAT_ORDER[a?.heat_risk?.level] ?? -1)
    );
  }, [data]);

  const summary = data?.summary || {};
  const maxLevel = summary.max_heat_level || 'low';
  const advisory = headerAdvisory(summary, maxLevel);
  const highHeat = Number(summary.workers_high_heat) || 0;
  const rainSites = Number(summary.sites_rain_expected) || 0;
  const uvElevated = ['high', 'very_high', 'extreme'].includes(summary.peak_uv_level);
  const freshnessRaw = data?.generated_at ? formatRelativeTime(data.generated_at) : '';
  const freshness =
    freshnessRaw && freshnessRaw !== 'N/A' && freshnessRaw !== 'Invalid Date' ? freshnessRaw : '';

  return (
    <section aria-label="Site weather and heat advisory">
      <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
        <div className="mb-4 sm:mb-6">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <Sun className="w-5 h-5 sm:w-6 sm:h-6 text-orange-500 flex-shrink-0" aria-hidden="true" />
              <h2 className="text-lg sm:text-2xl font-bold text-gray-900 min-w-0 truncate">
                Site Weather &amp; Heat Advisory
              </h2>
            </div>
            {data?.available && (
              <div className="flex-shrink-0">
                <StatusBadge status={advisory.status} label={advisory.label} />
              </div>
            )}
          </div>
          <p className="text-xs sm:text-sm text-gray-600 mt-1 ml-7 sm:ml-8">
            Live NEA readings at sites with workers clocked in
            {freshness && (
              <span className="text-gray-400"> · Updated {freshness.toLowerCase()}</span>
            )}
          </p>
        </div>

        {isLoading ? (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6 animate-pulse">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="metrics-card h-full">
                <div className="metrics-card-content">
                  <div className="metrics-card-icon bg-gray-200" />
                  <div className="metrics-card-text space-y-2">
                    <div className="h-5 sm:h-6 w-10 sm:w-12 bg-gray-200 rounded" />
                    <div className="h-3 sm:h-4 w-20 sm:w-24 bg-gray-200 rounded" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : !data?.available ? (
          <div className="text-center py-12">
            <AlertTriangle className="w-12 h-12 text-gray-400 mx-auto mb-4" aria-hidden="true" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Live weather unavailable</h3>
            <p className="text-sm text-gray-500">NEA readings could not be loaded. Try again in a few minutes.</p>
          </div>
        ) : sites.length === 0 ? (
          <div className="text-center py-12">
            <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" aria-hidden="true" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No workers currently on site</h3>
            <p className="text-sm text-gray-500">
              Heat and rain advisories appear here when crews clock in on a mapped worksite.
            </p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
              <SummaryCard
                icon={Users}
                iconBg="bg-blue-100"
                iconFg="text-blue-800"
                value={fmtCount(summary.workers_on_site)}
                label="Workers on site"
                title="Workers currently clocked in on a mapped site"
              />
              <SummaryCard
                icon={Thermometer}
                iconBg={highHeat > 0 ? 'bg-orange-100' : 'bg-green-100'}
                iconFg={highHeat > 0 ? 'text-orange-600' : 'text-green-600'}
                value={fmtCount(highHeat)}
                label="High heat"
                title="Workers on a site in high or extreme heat"
                valueClass={highHeat > 0 ? 'text-orange-700' : ''}
              />
              <SummaryCard
                icon={CloudRain}
                iconBg="bg-blue-100"
                iconFg="text-blue-600"
                value={fmtCount(rainSites)}
                label="Sites with rain"
                title="Sites where rain is falling or expected in the next two hours"
                valueClass={rainSites > 0 ? 'text-blue-700' : ''}
              />
              <SummaryCard
                icon={Sun}
                iconBg="bg-amber-100"
                iconFg="text-amber-600"
                value={summary.peak_uv_index != null ? summary.peak_uv_index : '–'}
                label="Peak UV"
                detail={summary.peak_uv_label && summary.peak_uv_index != null ? summary.peak_uv_label : null}
                title={
                  summary.peak_uv_index != null
                    ? `Peak UV ${summary.peak_uv_index} · ${summary.peak_uv_label || 'this hour'} (NEA, islandwide)`
                    : 'Peak UV unavailable'
                }
                valueClass={uvElevated ? 'text-amber-700' : ''}
              />
            </div>

            <div className="space-y-3 max-h-80 overflow-y-auto pr-1 mt-6">
              {sites.map((s, idx) => {
                const level = s?.heat_risk?.level || 'unknown';
                return (
                  <div
                    key={s.site_id ? `ws-${s.site_id}` : `pj-${s.project_code}-${idx}`}
                    className="p-4 bg-white border border-gray-200 rounded-xl hover:border-gray-300 hover:shadow-sm transition-all"
                    title={s?.heat_risk?.advice || ''}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-gray-900 truncate flex items-center gap-2 min-w-0">
                          <MapPin className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" aria-hidden="true" />
                          <span className="truncate">{s.site_name}</span>
                          <CountBadge
                            count={s.headcount || 0}
                            theme="admin"
                            max={9999}
                            className="flex-shrink-0"
                          />
                        </p>
                        <p className="text-xs text-gray-500 truncate">
                          {s.project_code ? `${s.project_code} · ` : ''}
                          {s.forecast || '—'}
                        </p>
                      </div>

                      <div className="flex items-center gap-3 flex-shrink-0 text-xs text-gray-600">
                        <span className="hidden lg:flex items-center gap-1" title="Air temperature">
                          <Thermometer className="w-3.5 h-3.5 text-gray-400" aria-hidden="true" />
                          {fmtTemp(s.temperature_c)}
                        </span>
                        <span className="hidden lg:flex items-center gap-1" title="Humidity">
                          <Droplets className="w-3.5 h-3.5 text-gray-400" aria-hidden="true" />
                          {s.humidity_pct != null ? `${Math.round(s.humidity_pct)}%` : '–'}
                        </span>
                        <span className="hidden lg:flex items-center gap-1" title="UV index">
                          <Sun className="w-3.5 h-3.5 text-gray-400" aria-hidden="true" />
                          UV {s.uv_index != null ? s.uv_index : '–'}
                        </span>
                        {(s.raining_now || s.rain_expected) && (
                          <span
                            className="hidden lg:flex items-center gap-1 text-blue-600"
                            title={s.raining_now ? 'Raining now at nearest station' : 'Rain expected'}
                            aria-label={s.raining_now ? 'Raining now' : 'Rain expected'}
                          >
                            <CloudRain className="w-3.5 h-3.5" aria-hidden="true" />
                            {s.raining_now ? 'Now' : ''}
                          </span>
                        )}
                        <StatusBadge
                          status={HEAT_STATUS[level] || 'heat_unknown'}
                          label={s?.heat_risk?.label}
                        />
                      </div>
                    </div>

                    <div className="flex lg:hidden flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-xs text-gray-600">
                      <span className="flex items-center gap-1" title="Air temperature">
                        <Thermometer className="w-3.5 h-3.5 text-gray-400" aria-hidden="true" />
                        {fmtTemp(s.temperature_c)}
                      </span>
                      <span className="flex items-center gap-1" title="Humidity">
                        <Droplets className="w-3.5 h-3.5 text-gray-400" aria-hidden="true" />
                        {s.humidity_pct != null ? `${Math.round(s.humidity_pct)}%` : '–'}
                      </span>
                      <span className="flex items-center gap-1" title="UV index">
                        <Sun className="w-3.5 h-3.5 text-gray-400" aria-hidden="true" />
                        UV {s.uv_index != null ? s.uv_index : '–'}
                      </span>
                      {(s.raining_now || s.rain_expected) && (
                        <span
                          className="flex items-center gap-1 text-blue-600"
                          title={s.raining_now ? 'Raining now at nearest station' : 'Rain expected'}
                          aria-label={s.raining_now ? 'Raining now' : 'Rain expected'}
                        >
                          <CloudRain className="w-3.5 h-3.5" aria-hidden="true" /> {s.raining_now ? 'Raining' : 'Rain'}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            <p className="text-[11px] text-gray-400 mt-3">
              Heat risk is an advisory estimate (apparent temperature from air temp + humidity), not a WBGT measurement.
            </p>
          </>
        )}
      </div>
    </section>
  );
};

export default SiteWeatherSection;
